<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { capitalize } from '@sancare/typescript-commons/src/misc/string-utils'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useStore } from '@/store'
import { Stay } from '@/store/modules/stay/types'

import StayActions from './StayActions.vue'

const { t } = useI18n()
const store = useStore()
const props = defineProps<{
  stay: Stay
}>()

const actionsLoadedOnce = ref(false)

const syncStayRequest = computed(() => store.state.stayDisplayer.syncStayToExternalConnectorRequest)
const fetchStayActionsRequest = computed(() => store.state.mcoStay.requests.fetchStayActions)
const currentActions = computed(() => store.state.mcoStay.currentActions)
const connectorTypes = computed(() => store.state.settings.connectorSettings.connectorTypes)

const buttonLabel = computed(() => {
  if (syncStayRequest.value.fetching) {
    return 'En cours de synchronisation'
  }
  if (syncStayRequest.value.ok) {
    return 'Séjour synchronisé'
  }
  if (syncStayRequest.value.error) {
    return 'La synchronisation a échoué'
  }
  if (connectorTypes.value.length) {
    const connectorLabel = connectorTypes.value.map((connectorType) => capitalize(connectorType)).join(` ${t('or')} `)
    return `Synchroniser le séjour vers ${connectorLabel}`
  }
  return ''
})

const isSyncDisabled = computed(() => syncStayRequest.value.ok || syncStayRequest.value.error)
function syncStay() {
  if (syncStayRequest.value.fetching || isSyncDisabled.value) {
    return
  }
  store.dispatch('syncStayToExternalConnector', { stayId: props.stay.id })
  actionsLoadedOnce.value = false
}

function showStayActions() {
  if (actionsLoadedOnce.value) {
    return
  }
  store.dispatch('mcoStay/fetchStayActions', props.stay.id)
  actionsLoadedOnce.value = true
}
</script>

<template>
  <v-tooltip
    v-if="buttonLabel !== ''"
    placement="bottom"
    @show="showStayActions"
  >
    <div
      class="btn btn-icon__action"
      :class="{
        'loading': syncStayRequest.fetching,
        'active': syncStayRequest.ok,
        'error': syncStayRequest.error,
        'sync-disabled': isSyncDisabled
      }"
      @click="syncStay()"
    >
      <sancare-octicon
        v-if="syncStayRequest.ok"
        name="check"
      />
      <sancare-octicon
        v-else-if="syncStayRequest.error"
        name="alert"
      />
      <div
        v-else-if="syncStayRequest.fetching"
        class="loader loader-xs loader-purple"
      />
      <sancare-octicon
        v-else
        name="sync"
      />
    </div>
    <template #popper>
      <div class="text-align">
        {{ buttonLabel }}
        <stay-actions
          :is-loading="fetchStayActionsRequest.fetching"
          :stay-actions="currentActions"
        />
      </div>
    </template>
  </v-tooltip>
</template>

<style scoped lang="scss">
@import '@sancare/typescript-commons/src/assets/styles/variables';
@import '@/assets/styles/mco/stay-displayer-actions';

.btn-icon__action.sync-disabled {
  cursor: not-allowed;
}

.btn-icon__action.error {
  @include btn-color($sancare-red, #fff);
}
</style>
